import { Employee, EmployeeStatus, Company, User } from '../../types';
import { getProbationPeriodFromHiring } from '../../probationPeriod';
import { sentenceCasePtBr, canonicalizeEmployeeRole } from '../../utils/textFormat';
import { normalizeSettingsTabsFromDb } from '../../utils/permissions';
import type {
  RhEmployeeRow,
  RhAbsenceRow,
  RhSituationRow,
  RhDelayRow,
  RhEarlyDepartureRow,
  RhUserRow,
} from '../rhDataSchemas';

/**
 * Converte linha rh_employees + ocorrências relacionadas para o tipo Employee do app.
 * @param row - Linha validada do Postgres
 * @param absences - Ausências da empresa (filtradas por employee_id)
 * @param situations - Situações da empresa (filtradas por employee_id)
 * @param delays - Atrasos da empresa (filtradas por employee_id)
 * @param earlyDepartures - Saídas antecipadas da empresa (filtradas por employee_id)
 */
export function toAppEmployee(
  row: RhEmployeeRow,
  absences: RhAbsenceRow[],
  situations: RhSituationRow[],
  delays: RhDelayRow[] = [],
  earlyDepartures: RhEarlyDepartureRow[] = [],
): Employee {
  const empAbsences = absences
    .filter((a) => a.employee_id === row.id)
    .map((a) => ({
      id: a.id,
      date: a.date,
      reason: a.reason,
      createdBy: a.created_by ?? undefined,
      createdByUsername: a.created_by_username ?? undefined,
      createdAt: a.created_at ?? undefined,
    }));
  const empDelays = delays
    .filter((d) => d.employee_id === row.id)
    .map((d) => ({
      id: d.id,
      date: d.date,
      reason: d.reason,
      createdBy: d.created_by ?? undefined,
      createdByUsername: d.created_by_username ?? undefined,
      createdAt: d.created_at ?? undefined,
    }));
  const empEarlyDepartures = earlyDepartures
    .filter((ed) => ed.employee_id === row.id)
    .map((ed) => ({
      id: ed.id,
      date: ed.date,
      reason: ed.reason,
      createdBy: ed.created_by ?? undefined,
      createdByUsername: ed.created_by_username ?? undefined,
      createdAt: ed.created_at ?? undefined,
    }));
  const empSituations = situations
    .filter((s) => s.employee_id === row.id)
    .map((s) => ({
      id: s.id,
      date: s.date,
      description: s.description,
      createdBy: s.created_by ?? undefined,
      createdByUsername: s.created_by_username ?? undefined,
      createdAt: s.created_at ?? undefined,
    }));

  return {
    id: row.id,
    name: row.name,
    role: canonicalizeEmployeeRole(row.role),
    avatar: row.avatar ?? '',
    status: row.status as EmployeeStatus,
    probationPeriod: getProbationPeriodFromHiring(row.hiring_date ?? undefined),
    absences: empAbsences,
    delays: empDelays,
    earlyDepartures: empEarlyDepartures,
    situations: empSituations,
    code: row.employee_code ?? undefined,
    hiringDate: row.hiring_date ?? undefined,
    birthDate: row.birth_date ?? undefined,
    whatsapp: row.whatsapp ?? undefined,
    lastBirthdayMsgYear: row.last_birthday_msg_year ?? undefined,
    costCenterCode: row.cost_center_code ?? undefined,
    costCenterDescription:
      row.cost_center_description != null && row.cost_center_description !== ''
        ? sentenceCasePtBr(row.cost_center_description)
        : undefined,
  };
}

/**
 * Converte linha rh_users + empresas vinculadas para User do app.
 * @param row - Linha validada (sem password_hash)
 * @param companies - Nomes das empresas permitidas
 */
export function toAppUser(row: RhUserRow, companies: string[]): User {
  return {
    id: row.id,
    username: row.username,
    permissions: {
      dashboard: row.perm_dashboard,
      hr: row.perm_hr,
      settings: row.perm_settings,
      absences: row.perm_absences,
      employeeCreate: row.perm_employee_create ?? true,
      employeeEdit: row.perm_employee_edit ?? true,
      employeeDelete: row.perm_employee_delete ?? true,
      settingsTabs: normalizeSettingsTabsFromDb(row.perm_settings, row.perm_settings_detail),
    },
    allowedCompanies: companies as Company[],
  };
}
